import React from 'react';
import { motion } from 'framer-motion';
import {
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
  XCircleIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';

const STATUS_CONFIG = {
  pending_clearance: {
    icon: ClockIcon,
    iconColor: 'text-amber-500',
    iconBg: 'bg-amber-50 dark:bg-amber-500/10',
    label: 'Pending Clearance',
    accent: 'border-l-amber-500',
  },
  under_review: {
    icon: EyeIcon,
    iconColor: 'text-blue-500',
    iconBg: 'bg-blue-50 dark:bg-blue-500/10',
    label: 'Under Review',
    accent: 'border-l-blue-500',
  },
  cleared: {
    icon: CheckCircleIcon,
    iconColor: 'text-emerald-500',
    iconBg: 'bg-emerald-50 dark:bg-emerald-500/10',
    label: 'Cleared',
    accent: 'border-l-emerald-500',
  },
  frozen: {
    icon: LockClosedIcon,
    iconColor: 'text-red-500',
    iconBg: 'bg-red-50 dark:bg-red-500/10',
    label: 'Frozen',
    accent: 'border-l-red-500',
  },
  cancelled: {
    icon: XCircleIcon,
    iconColor: 'text-gray-400 dark:text-[#666666]',
    iconBg: 'bg-gray-100 dark:bg-white/5',
    label: 'Cancelled',
    accent: 'border-l-gray-300 dark:border-l-[#333]',
  },
  unpaid: {
    icon: ExclamationTriangleIcon,
    iconColor: 'text-gray-400 dark:text-[#666666]',
    iconBg: 'bg-gray-100 dark:bg-white/5',
    label: 'Awaiting Payment',
    accent: 'border-l-gray-300 dark:border-l-[#333]',
  },
  processing: {
    icon: ClockIcon,
    iconColor: 'text-gray-400 dark:text-[#666666]',
    iconBg: 'bg-gray-100 dark:bg-white/5',
    label: 'Processing',
    accent: 'border-l-gray-300 dark:border-l-[#333]',
  },
};

export default function PayoutStatusCard({ payoutStatus, compact = false }) {
  if (!payoutStatus) return null;

  const config = STATUS_CONFIG[payoutStatus.status] || STATUS_CONFIG.processing;
  const Icon = config.icon;

  if (compact) {
    return (
      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] rounded-full font-medium ${config.iconBg}`}>
        <Icon className={`w-3 h-3 ${config.iconColor}`} />
        <span className="text-gray-600 dark:text-gray-300">{config.label}</span>
      </span>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white dark:bg-[#111111] rounded-xl border border-gray-100 dark:border-[#1A1A1A] border-l-4 ${config.accent} overflow-hidden`}
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${config.iconBg}`}>
            <Icon className={`w-4 h-4 ${config.iconColor}`} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-medium text-gray-900 dark:text-white">
              {config.label}
            </p>
            <p className="text-xs text-gray-500 dark:text-[#999999] mt-0.5 leading-relaxed">
              {payoutStatus.message}
            </p>

            {payoutStatus.status === 'pending_clearance' && payoutStatus.daysRemaining !== undefined && (
              <div className="mt-3 flex items-center gap-2.5">
                <div className="flex-1 h-1 bg-gray-100 dark:bg-[#1A1A1A] rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-brand rounded-full"
                    initial={{ width: 0 }}
                    animate={{
                      width: `${Math.max(5, 100 - (payoutStatus.daysRemaining / 7) * 100)}%`,
                    }}
                    transition={{ duration: 0.5, ease: 'easeOut' }}
                  />
                </div>
                <span className="text-[10px] text-gray-400 dark:text-[#666666] flex-shrink-0 tabular-nums">
                  {payoutStatus.daysRemaining}d
                </span>
              </div>
            )}

            {payoutStatus.payoutAmount !== undefined && (
              <div className="mt-2 flex items-center gap-3 text-xs">
                <span className="text-gray-500 dark:text-[#999999]">
                  Payout <span className="font-medium text-gray-700 dark:text-gray-300">${payoutStatus.payoutAmount?.toFixed(2)}</span>
                </span>
                {payoutStatus.reserveAmount > 0 && (
                  <span className="text-amber-600 dark:text-amber-400">
                    ${payoutStatus.reserveAmount?.toFixed(2)} held
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
